import { useState } from "react";
import { ExternalLink, Linkedin, X } from "lucide-react";
import { resolveMediaUrl } from "@/lib/media-api";

export function speakerSubtitle(speaker) {
  return [speaker?.title, speaker?.company].filter(Boolean).join(" · ") || "TBA";
}

export function speakerHasProfile(speaker) {
  return Boolean(
    String(speaker?.bio || "").trim()
      || String(speaker?.website || "").trim()
      || String(speaker?.linkedin || "").trim(),
  );
}

export function speakersGridColsClass(count) {
  const n = Math.min(Math.max(Number(count) || 1, 1), 4);
  return ({ 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" })[n];
}

function speakerInitials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function SpeakerProfileModal({ speaker, onClose, accent = "var(--primary)" }) {
  if (!speaker) return null;
  const subtitle = speakerSubtitle(speaker);
  const photoUrl = resolveMediaUrl(speaker.photo || speaker.headshot || speaker.image);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="surface w-full max-w-lg border border-[var(--border-default)] shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="speaker-profile-title"
      >
        <div className="relative aspect-[16/9] bg-[var(--bg-elev)] overflow-hidden">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-full h-full object-cover object-top" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 35%, transparent), var(--bg-elev))` }}
            >
              <span className="font-heading text-5xl opacity-40">{speakerInitials(speaker.name)}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-surface)] via-transparent to-transparent" />
          <button
            type="button"
            className="absolute top-3 right-3 p-2 border border-[var(--border-default)] bg-[var(--bg-surface)]/80 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            onClick={onClose}
            aria-label="Close profile"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            {speaker.kind || "Speaker"}
          </div>
          <h3 id="speaker-profile-title" className="font-heading text-2xl mt-1">{speaker.name || "Speaker"}</h3>
          {subtitle !== "TBA" ? (
            <div className="text-sm text-[var(--text-secondary)] mt-1">{subtitle}</div>
          ) : null}
          {speaker.bio ? (
            <p className="text-sm text-[var(--text-secondary)] mt-4 leading-relaxed whitespace-pre-line">{speaker.bio}</p>
          ) : null}
          <div className="mt-5 flex flex-wrap gap-2">
            {speaker.website ? (
              <a
                href={speaker.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-2 border border-[var(--border-default)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]"
              >
                Website <ExternalLink size={12} />
              </a>
            ) : null}
            {speaker.linkedin ? (
              <a
                href={speaker.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-2 border border-[var(--border-default)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]"
              >
                LinkedIn <Linkedin size={12} />
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SpeakersGrid({
  speakers = [],
  max = 8,
  accent = "var(--primary)",
  emptyMessage = "Speaker lineup will be announced soon.",
  className = "",
}) {
  const [profile, setProfile] = useState(null);
  const rows = (Array.isArray(speakers) ? speakers : []).slice(0, max);

  if (!rows.length) {
    return (
      <div className={`border border-[var(--border-default)] bg-[var(--bg-surface)] p-8 text-sm text-[var(--text-tertiary)] ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  const colsClass = speakersGridColsClass(Math.min(rows.length, 4));

  return (
    <>
      <div
        className={`grid ${colsClass} gap-px bg-[var(--border-default)] border border-[var(--border-default)] ${className}`}
      >
        {rows.map((speaker, index) => {
          const photoUrl = resolveMediaUrl(speaker.photo || speaker.headshot || speaker.image);
          const hasProfile = speakerHasProfile(speaker);
          const subtitle = speakerSubtitle(speaker);
          const CardTag = hasProfile ? "button" : "div";

          return (
            <CardTag
              key={speaker.id || `${speaker.name}-${index}`}
              type={hasProfile ? "button" : undefined}
              className={`group relative aspect-[3/4] overflow-hidden bg-[var(--bg-surface)] text-left w-full ${
                hasProfile ? "cursor-pointer surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]" : ""
              }`}
              onClick={
                hasProfile
                  ? (ev) => {
                      ev.stopPropagation();
                      setProfile(speaker);
                    }
                  : undefined
              }
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    background: `linear-gradient(160deg, color-mix(in srgb, ${accent} 28%, var(--bg-elev)), var(--bg-elev))`,
                  }}
                >
                  <span className="font-heading text-4xl md:text-5xl opacity-35">{speakerInitials(speaker.name)}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <div className="font-heading text-base md:text-lg leading-tight text-white">{speaker.name || "Speaker"}</div>
                <div className="text-[10px] md:text-xs text-white/70 font-mono mt-1">{subtitle}</div>
                {hasProfile ? (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    View profile
                  </div>
                ) : null}
              </div>
            </CardTag>
          );
        })}
      </div>
      <SpeakerProfileModal speaker={profile} onClose={() => setProfile(null)} accent={accent} />
    </>
  );
}
